// ============================================================================
// BATTED BALL TAB — hitter batted-ball profile
// ============================================================================
// Dedicated PlayerDetail tab surfacing the batted-ball data we already ingest:
//  • Spray chart with REAL hit locations (event `sa` spray angle, 2026+)
//  • Exit velo × launch angle scatter with barrel zone
//  • Exit velo distribution histogram
//  • Trajectory profile (GB/LD/FB/PU) vs league average
//  • Damage by pitch type (AVG / SLG / EV / hard-hit)
// All computed client-side from pitchEvents (already loaded by PlayerDetail),
// with pitch-type / handedness / result filters.
// ============================================================================

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Card, Chip, Grid, Typography, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableHead, TableRow, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import {
  ScatterChart, Scatter, BarChart, Bar, XAxis, YAxis, ZAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, ReferenceArea, ReferenceLine, Cell,
} from 'recharts';
import SprayChart from '../charts/SprayChart';
import AtBatDetailDialog from '../AtBatDetailDialog';
import { pitchSplitsApi } from '../../services/apiService';
import { PITCH_NAMES, PITCH_COLORS } from '../../utils/pitchTypes';

// League-average trajectory mix (MLB, approximate steady-state)
const LEAGUE_TRAJ = { gb: 44.0, ld: 20.5, fb: 27.0, pu: 8.5 };

const HIT_RESULTS = new Set(['single', 'double', 'triple', 'home_run', 'home run']);
const BASES = { single: 1, double: 2, triple: 3, home_run: 4, 'home run': 4 };

const RESULT_COLORS = {
  home_run: '#e74c3c',
  triple: '#9b59b6',
  double: '#3498db',
  single: '#2ecc71',
  out: '#8a8a8a',
};

function classifyResult(abr) {
  const r = (abr || '').toLowerCase();
  if (r.includes('home')) return 'home_run';
  if (r === 'triple') return 'triple';
  if (r === 'double') return 'double';
  if (r === 'single') return 'single';
  return 'out';
}

function classifyTraj(e) {
  const t = (e.traj || '').toLowerCase();
  if (t) {
    if (t.includes('ground')) return 'gb';
    if (t.includes('line')) return 'ld';
    if (t.includes('fly')) return 'fb';
    if (t.includes('pop')) return 'pu';
  }
  const la = e.la;
  if (la == null) return null;
  if (la < 10) return 'gb';
  if (la < 25) return 'ld';
  if (la < 50) return 'fb';
  return 'pu';
}

const BattedBallTab = ({ player, pitchData, pitchEvents = [], year }) => {
  const theme = useTheme();
  const batterHand = player?.batSide || player?.bats || 'R';

  const [ptFilter, setPtFilter] = useState('all');
  const [handFilter, setHandFilter] = useState('all'); // pitcher hand faced
  const [resultFilter, setResultFilter] = useState('all');

  // ── At-bat detail popup (click a batted ball to see the full AB) ─────────
  const [abDialogOpen, setAbDialogOpen] = useState(false);
  const [abDialogLoading, setAbDialogLoading] = useState(false);
  const [abDialogError, setAbDialogError] = useState('');
  const [selectedAtBat, setSelectedAtBat] = useState(null);

  // ── EV × LA scatter zoom (drag to select a region, chip to reset) ───────
  const EV_DOMAIN = [55, 120];
  const LA_DOMAIN = [-40, 70];
  const [scatterZoom, setScatterZoom] = useState(null); // {x:[a,b], y:[a,b]}
  const [refArea, setRefArea] = useState(null);         // {x1,y1,x2,y2} while dragging

  const handleScatterMouseDown = useCallback((e) => {
    if (e?.xValue == null || e?.yValue == null) return;
    setRefArea({ x1: e.xValue, y1: e.yValue, x2: e.xValue, y2: e.yValue });
  }, []);
  const handleScatterMouseMove = useCallback((e) => {
    if (e?.xValue == null || e?.yValue == null) return;
    setRefArea(prev => (prev ? { ...prev, x2: e.xValue, y2: e.yValue } : prev));
  }, []);
  const handleScatterMouseUp = useCallback(() => {
    setRefArea(prev => {
      if (prev && Math.abs(prev.x2 - prev.x1) > 2 && Math.abs(prev.y2 - prev.y1) > 4) {
        setScatterZoom({
          x: [Math.min(prev.x1, prev.x2), Math.max(prev.x1, prev.x2)],
          y: [Math.min(prev.y1, prev.y2), Math.max(prev.y1, prev.y2)],
        });
      }
      return null;
    });
  }, []);

  const handleDotClick = useCallback(async (event) => {
    if (!event || !player) return;
    setAbDialogOpen(true);
    setAbDialogLoading(true);
    setAbDialogError('');
    setSelectedAtBat(null);
    try {
      const season = year || new Date().getFullYear();
      const filters = { page: 1, pageSize: 50 };
      if (event.gameDate) filters.gameDate = event.gameDate;
      if (event.opponent) filters.opponent = event.opponent;
      if (event.inning) filters.inning = event.inning;
      if (event.opponentName) filters.vsPlayer = event.opponentName;

      const response = await pitchSplitsApi.getAtBats('batter', player.team, player.name, season, filters);
      const atBats = response?.atBats || [];
      let match = atBats.find((ab) =>
        (!event.gameId || ab.gameId === event.gameId) &&
        (!event.gameDate || ab.gameDate === event.gameDate) &&
        (!event.opponent || ab.opponent === event.opponent) &&
        (!event.inning || ab.inning === event.inning)
      );
      if (!match && event.opponentName) {
        match = atBats.find((ab) =>
          (!event.gameDate || ab.gameDate === event.gameDate) &&
          (!event.opponent || ab.opponent === event.opponent) &&
          ab.opponentName === event.opponentName
        );
      }
      if (!match) match = atBats[0] || null;

      if (!match) {
        setAbDialogError('No matching at-bat found for this batted ball.');
      } else {
        setSelectedAtBat(match);
      }
    } catch (error) {
      setAbDialogError('Failed to load at-bat details.');
    } finally {
      setAbDialogLoading(false);
    }
  }, [player, year]);

  // ── In-play events (last pitch of AB, ball put in play) ──────────────────
  // The pitch-events endpoint returns expanded long field names; older/raw
  // sources use compact codes. Normalize once here.
  const inPlayAll = useMemo(() => (pitchEvents || [])
    .filter(e => (e.result || e.r) === 'inplay' && (e.isLastPitch ?? e.last) !== false)
    .map(e => ({
      pt: e.pitchType || e.pt || null,
      ev: parseFloat(e.exitVelocity ?? e.ev ?? 0) || 0,
      la: (e.launchAngle ?? e.la) != null ? parseFloat(e.launchAngle ?? e.la) : null,
      dist: parseFloat(e.distance ?? e.dist ?? 0) || 0,
      traj: e.trajectory || e.traj || null,
      sa: (e.sprayAngle ?? e.sa) != null ? parseFloat(e.sprayAngle ?? e.sa) : null,
      abr: e.atBatResult || e.abr || null,
      h: e.opponentHand || e.h || null,
      z: e.zone ?? e.z ?? 0,
      g: e.gameId || e.g || null,
      gd: e.gameDate || e.gd || null,
      i: e.inning ?? e.i ?? null,
      opp: e.opponent || e.opp || null,
      opn: e.opponentName || e.opn || null,
    })), [pitchEvents]);

  const pitchTypesPresent = useMemo(() => {
    const s = new Set();
    inPlayAll.forEach(e => { if (e.pt) s.add(e.pt); });
    return [...s].sort();
  }, [inPlayAll]);

  const filtered = useMemo(() => inPlayAll.filter(e => {
    if (ptFilter !== 'all' && e.pt !== ptFilter) return false;
    if (handFilter !== 'all' && e.h !== handFilter) return false;
    if (resultFilter !== 'all') {
      const res = classifyResult(e.abr);
      if (resultFilter === 'hits' && !HIT_RESULTS.has(res)) return false;
      if (resultFilter === 'hr' && res !== 'home_run') return false;
      if (resultFilter === 'outs' && HIT_RESULTS.has(res)) return false;
    }
    return true;
  }), [inPlayAll, ptFilter, handFilter, resultFilter]);

  const realSprayCount = useMemo(() => filtered.filter(e => e.sa != null).length, [filtered]);

  // ── Aggregate batted-ball profile from filtered events ───────────────────
  const agg = useMemo(() => {
    let gb = 0, ld = 0, fb = 0, pu = 0, total = 0;
    let evSum = 0, evN = 0, laSum = 0, laN = 0, distSum = 0, distN = 0;
    let hardHit = 0, barrels = 0;
    const spray = { pull: { count: 0, hits: 0 }, center: { count: 0, hits: 0 }, oppo: { count: 0, hits: 0 } };
    for (const e of filtered) {
      total++;
      const t = classifyTraj(e);
      if (t === 'gb') gb++; else if (t === 'ld') ld++; else if (t === 'fb') fb++; else if (t === 'pu') pu++;
      const ev = e.ev || 0;
      if (ev > 0) {
        evSum += ev; evN++;
        if (ev >= 95) hardHit++;
        if (ev >= 98 && e.la != null && e.la >= 26 && e.la <= 30) barrels++;
      }
      if (e.la != null) { laSum += e.la; laN++; }
      if (e.dist > 0) { distSum += e.dist; distN++; }
      if (e.sa != null) {
        // sa: negative = LF side. Pull = LF for RHB, RF for LHB.
        const lfSide = e.sa < -12, rfSide = e.sa > 12;
        const dir = lfSide ? (batterHand === 'L' ? 'oppo' : 'pull')
          : rfSide ? (batterHand === 'L' ? 'pull' : 'oppo')
          : 'center';
        spray[dir].count++;
        if (HIT_RESULTS.has(classifyResult(e.abr))) spray[dir].hits++;
      }
    }
    const pct = (n) => (total > 0 ? +((n / total) * 100).toFixed(1) : 0);
    return {
      total, groundBall: gb, lineDrive: ld, flyBall: fb, popup: pu,
      groundBallRate: pct(gb), lineDriveRate: pct(ld), flyBallRate: pct(fb), popupRate: pct(pu),
      avgExitVelo: evN > 0 ? +(evSum / evN).toFixed(1) : 0,
      avgLaunchAngle: laN > 0 ? +(laSum / laN).toFixed(1) : 0,
      avgDistance: distN > 0 ? Math.round(distSum / distN) : 0,
      hardHit, hardHitRate: evN > 0 ? +((hardHit / evN) * 100).toFixed(1) : 0,
      barrels, barrelRate: total > 0 ? +((barrels / total) * 100).toFixed(1) : 0,
      spray,
    };
  }, [filtered, batterHand]);

  // Event-derived aggregate is authoritative — stored batted_ball counts can
  // drift upward on re-ingestion. Only fall back to stored data when there
  // are no pitch events at all.
  const battedBall = useMemo(() => {
    if (inPlayAll.length === 0 && pitchData?.battedBall) return pitchData.battedBall;
    return agg;
  }, [agg, pitchData, inPlayAll.length]);

  // ── Spray chart dots — REAL spray angle when present ─────────────────────
  const sprayRawEvents = useMemo(() => filtered.map((e) => {
    let sprayAngle;
    if (e.sa != null) {
      sprayAngle = Math.max(-50, Math.min(50, e.sa));
    } else {
      // Fallback for pre-2026 data: approximate from strike-zone column
      const zone = e.z ?? 0;
      sprayAngle = zone >= 1 && zone <= 9 ? (((zone - 1) % 3) - 1) * 30 : 0;
      sprayAngle += (((parseInt(e.g, 10) || 0) % 13) - 6) * 2; // deterministic jitter
    }
    const dist = e.dist || 0;
    return {
      sprayAngle,
      normDist: dist > 0 ? Math.min(dist / 450, 1) : 0.35,
      exitVelocity: e.ev || 0,
      launchAngle: e.la ?? null,
      distance: dist,
      result: classifyResult(e.abr),
      gameId: e.g || null, gameDate: e.gd || null, inning: e.i || null,
      opponent: e.opp || null, opponentName: e.opn || null, pitchType: e.pt || null,
    };
  }), [filtered]);

  // ── EV × LA scatter ───────────────────────────────────────────────────────
  const scatterData = useMemo(() => filtered
    .filter(e => e.ev > 0 && e.la != null)
    .map(e => ({
      ev: e.ev, la: e.la, dist: e.dist || 0,
      result: classifyResult(e.abr), pt: e.pt,      gameId: e.g || null, gameDate: e.gd || null, inning: e.i || null,
      opponent: e.opp || null, opponentName: e.opn || null,    })), [filtered]);

  // ── EV histogram (5-mph buckets, 55–115) ─────────────────────────────────
  const evHistogram = useMemo(() => {
    const buckets = [];
    for (let lo = 55; lo < 115; lo += 5) buckets.push({ lo, label: `${lo}`, count: 0, hits: 0 });
    for (const e of filtered) {
      if (!e.ev) continue;
      const idx = Math.min(Math.max(Math.floor((e.ev - 55) / 5), 0), buckets.length - 1);
      buckets[idx].count++;
      if (HIT_RESULTS.has(classifyResult(e.abr))) buckets[idx].hits++;
    }
    return buckets;
  }, [filtered]);

  // ── Damage by pitch type ──────────────────────────────────────────────────
  const damageByPt = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      const pt = e.pt || 'UN';
      if (!map[pt]) map[pt] = { pt, bip: 0, hits: 0, bases: 0, hr: 0, evSum: 0, evN: 0, hardHit: 0 };
      const m = map[pt];
      m.bip++;
      const res = classifyResult(e.abr);
      if (HIT_RESULTS.has(res)) { m.hits++; m.bases += BASES[res] || 1; }
      if (res === 'home_run') m.hr++;
      if (e.ev > 0) { m.evSum += e.ev; m.evN++; if (e.ev >= 95) m.hardHit++; }
    }
    return Object.values(map)
      .filter(m => m.bip >= 3)
      .map(m => ({
        ...m,
        avg: m.bip > 0 ? (m.hits / m.bip).toFixed(3).replace(/^0/, '') : '—',
        slg: m.bip > 0 ? (m.bases / m.bip).toFixed(3).replace(/^0/, '') : '—',
        avgEv: m.evN > 0 ? (m.evSum / m.evN).toFixed(1) : '—',
        hardHitRate: m.evN > 0 ? ((m.hardHit / m.evN) * 100).toFixed(0) : '—',
      }))
      .sort((a, b) => b.bip - a.bip);
  }, [filtered]);

  // ── Trajectory rows vs league ─────────────────────────────────────────────
  const trajRows = [
    { key: 'gb', label: 'Ground Ball', pct: battedBall.groundBallRate || 0, league: LEAGUE_TRAJ.gb, color: '#8d6e63' },
    { key: 'ld', label: 'Line Drive', pct: battedBall.lineDriveRate || 0, league: LEAGUE_TRAJ.ld, color: '#66bb6a' },
    { key: 'fb', label: 'Fly Ball', pct: battedBall.flyBallRate || 0, league: LEAGUE_TRAJ.fb, color: '#42a5f5' },
    { key: 'pu', label: 'Popup', pct: battedBall.popupRate ?? (battedBall.total > 0 ? +(((battedBall.popup || 0) / battedBall.total) * 100).toFixed(1) : 0), league: LEAGUE_TRAJ.pu, color: '#ef5350' },
  ];

  if (inPlayAll.length === 0 && !pitchData?.battedBall) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 4, textAlign: 'center' }}>
        <SportsBaseballIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>No Batted Ball Data</Typography>
        <Typography color="text.secondary">
          Pitch-level batted ball data is not yet available for {player?.name || 'this player'} ({year}).
        </Typography>
      </Card>
    );
  }

  const cardSx = { border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, height: '100%' };

  return (
    <Grid container spacing={3}>
      {/* Filters */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                size="small" label="All Pitches"
                color={ptFilter === 'all' ? 'primary' : 'default'}
                variant={ptFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setPtFilter('all')}
              />
              {pitchTypesPresent.map(pt => (
                <Chip
                  key={pt} size="small" label={pt}
                  onClick={() => setPtFilter(ptFilter === pt ? 'all' : pt)}
                  variant={ptFilter === pt ? 'filled' : 'outlined'}
                  sx={ptFilter === pt ? { bgcolor: PITCH_COLORS[pt] || 'primary.main', color: '#fff' } : { borderColor: alpha(PITCH_COLORS[pt] || '#888', 0.5) }}
                />
              ))}
            </Box>
            <ToggleButtonGroup size="small" exclusive value={handFilter} onChange={(_, v) => v && setHandFilter(v)}>
              <ToggleButton value="all" sx={{ px: 1.5, py: 0.25 }}>vs All</ToggleButton>
              <ToggleButton value="L" sx={{ px: 1.5, py: 0.25 }}>vs LHP</ToggleButton>
              <ToggleButton value="R" sx={{ px: 1.5, py: 0.25 }}>vs RHP</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={resultFilter} onChange={(_, v) => v && setResultFilter(v)}>
              <ToggleButton value="all" sx={{ px: 1.5, py: 0.25 }}>All BIP</ToggleButton>
              <ToggleButton value="hits" sx={{ px: 1.5, py: 0.25 }}>Hits</ToggleButton>
              <ToggleButton value="hr" sx={{ px: 1.5, py: 0.25 }}>HR</ToggleButton>
              <ToggleButton value="outs" sx={{ px: 1.5, py: 0.25 }}>Outs</ToggleButton>
            </ToggleButtonGroup>
            <Chip size="small" variant="outlined" label={`${filtered.length} balls in play`} />
            {realSprayCount > 0 && (
              <Chip size="small" color="success" variant="outlined" label={`${realSprayCount} tracked locations`} />
            )}
          </Box>
        </Card>
      </Grid>

      {/* Spray chart — full-width row so the field + stat panel both breathe */}
      <Grid item xs={12}>
        <Card elevation={0} sx={cardSx}>
          <SprayChart
            battedBall={battedBall}
            batterHand={batterHand}
            perspective="batter"
            rawEvents={sprayRawEvents}
            onDotClick={handleDotClick}
          />
          {sprayRawEvents.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Click a ball to view the full at-bat
            </Typography>
          )}
          {realSprayCount === 0 && sprayRawEvents.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Approximate locations — tracked hit coordinates available for 2026 onward.
            </Typography>
          )}
        </Card>
      </Grid>

      {/* EV × LA scatter with barrel zone */}
      <Grid item xs={12} md={7}>
        <Card elevation={0} sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Exit Velocity × Launch Angle
            </Typography>
            {scatterZoom && (
              <Chip size="small" label="Reset zoom" onClick={() => setScatterZoom(null)} onDelete={() => setScatterZoom(null)} />
            )}
          </Box>
          {scatterData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No Statcast contact data for this selection
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={420}>
              <ScatterChart
                margin={{ top: 8, right: 12, bottom: 4, left: -12 }}
                onMouseDown={handleScatterMouseDown}
                onMouseMove={handleScatterMouseMove}
                onMouseUp={handleScatterMouseUp}
                onMouseLeave={() => setRefArea(null)}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.4)} />
                <XAxis type="number" dataKey="ev" name="Exit Velo" domain={scatterZoom ? scatterZoom.x : EV_DOMAIN} allowDataOverflow tick={{ fontSize: 13 }}
                  label={{ value: 'Exit Velo (mph)', position: 'insideBottom', offset: -2, fontSize: 13 }} />
                <YAxis type="number" dataKey="la" name="Launch Angle" domain={scatterZoom ? scatterZoom.y : LA_DOMAIN} allowDataOverflow tick={{ fontSize: 13 }}
                  label={{ value: 'LA (°)', angle: -90, position: 'insideLeft', fontSize: 13 }} />
                <ZAxis range={[60, 60]} />
                {/* Barrel zone: EV ≥ 98, LA 26–30 */}
                <ReferenceArea x1={98} x2={120} y1={26} y2={30} fill={alpha('#e74c3c', 0.15)} stroke={alpha('#e74c3c', 0.4)} strokeDasharray="4 3" />
                {/* Sweet spot band: LA 8–32 */}
                <ReferenceArea x1={55} x2={120} y1={8} y2={32} fill={alpha('#2ecc71', 0.05)} />
                <ReferenceLine x={95} stroke={alpha('#f39c12', 0.5)} strokeDasharray="4 3" />
                <ReTooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'capitalize' }}>
                          {(d.result || '').replace('_', ' ')}{d.pt ? ` — ${PITCH_NAMES[d.pt] || d.pt}` : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {d.ev} mph · {d.la}°{d.dist ? ` · ${d.dist} ft` : ''}
                        </Typography>
                      </Box>
                    );
                  }}
                />
                <Scatter
                  data={scatterData}
                  cursor="pointer"
                  onClick={(d) => d?.payload && handleDotClick(d.payload)}
                >
                  {scatterData.map((d, i) => (
                    <Cell key={i} fill={RESULT_COLORS[d.result] || RESULT_COLORS.out} fillOpacity={d.result === 'out' ? 0.45 : 0.9} />
                  ))}
                </Scatter>
                {/* Drag-selection rectangle */}
                {refArea && (
                  <ReferenceArea
                    x1={Math.min(refArea.x1, refArea.x2)} x2={Math.max(refArea.x1, refArea.x2)}
                    y1={Math.min(refArea.y1, refArea.y2)} y2={Math.max(refArea.y1, refArea.y2)}
                    fill={alpha(theme.palette.primary.main, 0.15)}
                    stroke={theme.palette.primary.main}
                    strokeDasharray="4 3"
                  />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, justifyContent: 'center' }}>
            {Object.entries({ 'HR': 'home_run', '3B': 'triple', '2B': 'double', '1B': 'single', 'Out': 'out' }).map(([lbl, key]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: RESULT_COLORS[key] }} />
                <Typography variant="caption" color="text.secondary">{lbl}</Typography>
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary">· red box = barrel zone · green band = sweet spot · click a dot for the at-bat · drag to zoom</Typography>
          </Box>
        </Card>
      </Grid>

      {/* Trajectory profile vs league */}
      <Grid item xs={12} md={5}>
        <Card elevation={0} sx={cardSx}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Trajectory Profile
          </Typography>
          {trajRows.map(row => (
            <Box key={row.key} sx={{ mb: 1.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="body2">{row.label}</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {row.pct}% <Typography component="span" variant="caption" color="text.secondary">(lg {row.league}%)</Typography>
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 10, borderRadius: 5, bgcolor: alpha(row.color, 0.15) }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(row.pct, 100)}%`, borderRadius: 5, bgcolor: row.color }} />
                <Box sx={{ position: 'absolute', left: `${Math.min(row.league, 100)}%`, top: -2, bottom: -2, width: 2, bgcolor: theme.palette.text.primary, opacity: 0.6 }} />
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Chip size="small" variant="outlined" label={`Avg EV ${battedBall.avgExitVelo || '—'} mph`} color={battedBall.avgExitVelo >= 90 ? 'success' : 'default'} />
            <Chip size="small" variant="outlined" label={`Avg LA ${battedBall.avgLaunchAngle ?? '—'}°`} />
            <Chip size="small" variant="outlined" label={`Hard Hit ${battedBall.hardHitRate || 0}%`} color={battedBall.hardHitRate >= 40 ? 'success' : 'default'} />
            <Chip size="small" variant="outlined" label={`Barrel ${battedBall.barrelRate || 0}%`} color={battedBall.barrelRate >= 8 ? 'success' : 'default'} />
          </Box>
        </Card>
      </Grid>

      {/* EV histogram — full width for readable buckets */}
      <Grid item xs={12}>
        <Card elevation={0} sx={cardSx}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Exit Velocity Distribution
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={evHistogram} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.4)} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <ReTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{d.lo}–{d.lo + 5} mph</Typography>
                      <Typography variant="caption">{d.count} BIP · {d.hits} hits</Typography>
                    </Box>
                  );
                }}
              />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {evHistogram.map((b, i) => (
                  <Cell key={i} fill={b.lo >= 95 ? '#e74c3c' : b.lo >= 85 ? '#f39c12' : alpha(theme.palette.primary.main, 0.6)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            mph (bucket start) — orange 85+, red 95+ (hard hit)
          </Typography>
        </Card>
      </Grid>

      {/* Damage by pitch type */}
      {damageByPt.length > 0 && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Damage by Pitch Type
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pitch</TableCell>
                    <TableCell align="right">BIP</TableCell>
                    <TableCell align="right">AVG</TableCell>
                    <TableCell align="right">SLG</TableCell>
                    <TableCell align="right">HR</TableCell>
                    <TableCell align="right">Avg EV</TableCell>
                    <TableCell align="right">Hard Hit%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {damageByPt.map(row => (
                    <TableRow key={row.pt} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PITCH_COLORS[row.pt] || '#888' }} />
                          {PITCH_NAMES[row.pt] || row.pt}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{row.bip}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{row.avg}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{row.slg}</TableCell>
                      <TableCell align="right">{row.hr}</TableCell>
                      <TableCell align="right">{row.avgEv}</TableCell>
                      <TableCell align="right">{row.hardHitRate}{row.hardHitRate !== '—' ? '%' : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>
      )}

      {/* At-bat detail popup */}
      <AtBatDetailDialog
        open={abDialogOpen}
        onClose={() => setAbDialogOpen(false)}
        atBat={selectedAtBat}
        loading={abDialogLoading}
        error={abDialogError}
      />
    </Grid>
  );
};

export default BattedBallTab;
